import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { WebsocketEmitters } from '@common/app/socket';
// import { customToast } from '@base/utils';
// import { t } from 'i18next';
import { type Socket } from 'socket.io-client';

import { useAppSelector } from '@/redux/store';

import { socket } from './socket';

interface UseSocketIoReturnType {
  socketData: {
    isConnected: boolean;
    isReconnect: boolean;
  };
  setSocketData: Dispatch<
    SetStateAction<{
      isConnected: boolean;
      isReconnect: boolean;
    }>
  >;
  reconnectIntervalId: MutableRefObject<NodeJS.Timeout | null | undefined>;
}

const useSocketIo = (): UseSocketIoReturnType => {
  const [socketData, setSocketData] = useState({
    isConnected: false,
    isReconnect: false,
  });
  const reconnectIntervalId = useRef<NodeJS.Timeout | null>();
  const token = useAppSelector((state) => state.user.token);
  const tokenRef = useRef<string | null>(null);
  const prevTokenRef = useRef<string | null>(null);
  const authRetrySessionRef = useRef<string | null>(null);

  useEffect(() => {
    const onConnect = (): void => {
      console.log('connected!');
      socket.emit(WebsocketEmitters.AUTH, token);
      setSocketData({ isConnected: true, isReconnect: false });
    };

    const authOk = (user_id: string): void => {
      console.log('Authorization successful:', user_id);
    };
    const authFailure = (message: string): void => {
      console.error('Authorization failed:', message);
    };
    const authRequired = (): void => {
      console.log('~~~~ Authorization required ~~~~');
      setSocketData((state) => ({
        ...state,
        isConnected: false,
        isReconnect: true,
      }));

      const session = tokenRef.current;

      if (!session) {
        socket.disconnect();
        authRetrySessionRef.current = null;

        return;
      }

      // Force a new handshake with auth.session.
      // Guard: don't spin if backend keeps responding AUTH_REQUIRED for the same session.
      if (authRetrySessionRef.current === session) {
        return;
      }

      authRetrySessionRef.current = session;
      socket.auth = { session };
      socket.disconnect();
      setTimeout(() => {
        if (tokenRef.current) socket.connect();
      }, 0);
    };

    const onDisconnect = (reason: Socket.DisconnectReason): void => {
      console.log('disconnect!');

      if (reason === 'io server disconnect' && tokenRef.current) {
        socket.connect();
      }

      if (reason !== 'io client disconnect') {
        setSocketData((state) => ({ ...state, isReconnect: true }));
        // customToast({ kind: 'error', text: t('notification.socketClose') });
      }

      setSocketData((state) => ({ ...state, isConnected: false }));
    };

    const onConnectError = (): void => {
      console.log('web-socket connect error');

      if (reconnectIntervalId.current) {
        clearTimeout(reconnectIntervalId.current);
      }
      reconnectIntervalId.current = setTimeout(() => {
        if (tokenRef.current) {
          socket.auth = { session: tokenRef.current };
          socket.connect();
        }
      }, 3000);
    };

    socket.on('connect', onConnect);
    socket.on(WebsocketEmitters.AUTH_REQUIRED, authRequired);
    socket.on(WebsocketEmitters.AUTH_OK, authOk);
    socket.on(WebsocketEmitters.AUTH_FAILURE, authFailure);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);

    return (): void => {
      socket.off('connect', onConnect);
      socket.off(WebsocketEmitters.AUTH_REQUIRED, authRequired);
      socket.off(WebsocketEmitters.AUTH_OK, authOk);
      socket.off(WebsocketEmitters.AUTH_FAILURE, authFailure);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
    };
  }, []);

  useEffect(() => {
    tokenRef.current = token;

    if (!token) {
      prevTokenRef.current = null;
      socket.disconnect();

      return;
    }

    socket.auth = { session: token };

    // if session changed, we need a new handshake
    if (
      socket.connected &&
      prevTokenRef.current &&
      prevTokenRef.current !== token
    ) {
      socket.disconnect();
    }

    if (!socket.connected) {
      socket.connect();
    }

    prevTokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (socketData.isConnected && reconnectIntervalId.current) {
      clearTimeout(reconnectIntervalId.current);
      reconnectIntervalId.current = null;
    }
  }, [socketData.isConnected]);

  return { socketData, setSocketData, reconnectIntervalId };
};

export { useSocketIo, type UseSocketIoReturnType };
